/**
 * Mio auth flow state machine.
 *
 * Drives the 8-state journey signed off in C:/tmp/mio-auth-journey.html:
 *   1  signIn:start          (email + password)
 *   2  signIn:verifyCode     (OTP, new device)
 *   3  signIn:forgotPassword (email entry)
 *   4  signIn:resetPassword  (OTP + new password)
 *   5  signIn:useAnotherMethod
 *   6  signUp:start          (email + password + OAuth row)
 *   7  signUp:verifyEmail    (OTP)
 *   8  signUp:continueUsername (post-OAuth)
 *
 * Clerk's `signIn.create()` / `signUp.create()` resources determine the
 * NEXT screen via their `status` and `firstFactorVerification` fields;
 * `transitionFromClerkStatus` is the central place that maps those into
 * Mio's UI step. Everything else dispatches plain actions.
 */

"use client";

import { useReducer, useCallback } from "react";

export type AuthStep =
  | "signIn.start"
  | "signIn.verifyCode"
  | "signIn.forgotPassword"
  | "signIn.resetPassword"
  | "signIn.useAnotherMethod"
  | "signUp.start"
  | "signUp.verifyEmail"
  | "signUp.continueUsername";

export type AuthMode = "sign-in" | "sign-up";

/**
 * Which Clerk verifier the verify-code card should dispatch when the
 * user enters the 6-digit code.
 *
 *   "first"   → signIn.attemptFirstFactor   (email-as-alternative on a
 *                                            known device, picked from
 *                                            state 05)
 *   "second"  → signIn.attemptSecondFactor  (new-device MFA, status
 *                                            === needs_second_factor)
 *   null      → safe default = attemptFirstFactor (e.g. deep-link
 *                                                   landing on verify
 *                                                   without prior nav)
 */
export type VerifyMode = "first" | "second" | null;

export type AuthFlowState = {
  step: AuthStep;
  /** Identifier the user typed at start (email). Survives across steps for identity row. */
  identifier: string;
  /** Top-level error banner copy (null = no banner). Cleared on every transition. */
  error: string | null;
  /** True while a network request is in flight (CTA spinner + disable). */
  submitting: boolean;
  /** Last second the resend button was clicked (epoch ms). Drives countdown. */
  lastResendAt: number | null;
  /** How the verify-code card should attempt + resend. Set by whichever card navigates into verifyCode. */
  verifyMode: VerifyMode;
};

export type AuthFlowAction =
  | { type: "GO"; step: AuthStep }
  | { type: "SET_IDENTIFIER"; identifier: string }
  | { type: "SET_ERROR"; error: string | null }
  | { type: "SET_SUBMITTING"; submitting: boolean }
  | { type: "SET_VERIFY_MODE"; verifyMode: VerifyMode }
  | { type: "MARK_RESEND" }
  | { type: "RESET"; mode: AuthMode };

const initialFor = (mode: AuthMode): AuthFlowState => ({
  step: mode === "sign-in" ? "signIn.start" : "signUp.start",
  identifier: "",
  error: null,
  submitting: false,
  lastResendAt: null,
  verifyMode: null,
});

function reducer(state: AuthFlowState, action: AuthFlowAction): AuthFlowState {
  switch (action.type) {
    case "GO":
      return {
        ...state,
        step: action.step,
        error: null,
        submitting: false,
      };
    case "SET_IDENTIFIER":
      return { ...state, identifier: action.identifier };
    case "SET_ERROR":
      return { ...state, error: action.error, submitting: false };
    case "SET_SUBMITTING":
      return { ...state, submitting: action.submitting };
    case "SET_VERIFY_MODE":
      return { ...state, verifyMode: action.verifyMode };
    case "MARK_RESEND":
      return { ...state, lastResendAt: Date.now() };
    case "RESET":
      return initialFor(action.mode);
    default:
      return state;
  }
}

export function useAuthFlow(mode: AuthMode) {
  const [state, dispatch] = useReducer(reducer, mode, initialFor);

  const goTo = useCallback(
    (step: AuthStep) => dispatch({ type: "GO", step }),
    [],
  );
  const setIdentifier = useCallback(
    (identifier: string) => dispatch({ type: "SET_IDENTIFIER", identifier }),
    [],
  );
  const setError = useCallback(
    (error: string | null) => dispatch({ type: "SET_ERROR", error }),
    [],
  );
  const setSubmitting = useCallback(
    (submitting: boolean) =>
      dispatch({ type: "SET_SUBMITTING", submitting }),
    [],
  );
  const setVerifyMode = useCallback(
    (verifyMode: VerifyMode) =>
      dispatch({ type: "SET_VERIFY_MODE", verifyMode }),
    [],
  );
  const markResend = useCallback(() => dispatch({ type: "MARK_RESEND" }), []);
  const reset = useCallback(
    (resetMode: AuthMode = mode) =>
      dispatch({ type: "RESET", mode: resetMode }),
    [mode],
  );

  return {
    state,
    goTo,
    setIdentifier,
    setError,
    setSubmitting,
    setVerifyMode,
    markResend,
    reset,
  };
}

/**
 * Translate a Clerk ClerkAPIError into single-line copy for our banner.
 * The widget previously did this via `formFieldErrorText`; we own it now.
 */
export function readClerkError(err: unknown): string {
  if (!err || typeof err !== "object") return "Something went wrong. Try again.";
  const e = err as { errors?: Array<{ longMessage?: string; message?: string }>; message?: string };
  if (Array.isArray(e.errors) && e.errors.length > 0) {
    const first = e.errors[0];
    return first.longMessage || first.message || "Something went wrong.";
  }
  if (typeof e.message === "string") return e.message;
  return "Something went wrong. Try again.";
}
